import React, { useEffect, useState } from 'react';
import { getCurrentDrivers, getDriverStandings, getRaces, getRaceQualifying } from '../api';

const keyframes = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const ALPHA3_TO_ALPHA2 = {
  NED: 'nl', GBR: 'gb', MON: 'mc', ESP: 'es', AUS: 'au',
  CAN: 'ca', FRA: 'fr', ITA: 'it', NZL: 'nz', JPN: 'jp',
  BRA: 'br', DEU: 'de', THA: 'th', ARG: 'ar', FIN: 'fi',
  USA: 'us', MEX: 'mx', BEL: 'be', DNK: 'dk', CHN: 'cn',
};

function flagUrl(alpha3) {
  const a2 = ALPHA3_TO_ALPHA2[alpha3];
  return a2 ? `https://flagcdn.com/w20/${a2}.png` : null;
}

function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function DriverCard({ driver, animDelay, standing, qualiPos }) {
  const colour = driver.teamColour || '444444';
  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      background: '#111',
      borderRadius: 8,
      borderLeft: `4px solid #${colour}`,
      padding: 16,
      animation: 'fadeUp 0.4s ease both',
      animationDelay: `${animDelay}ms`,
    }}>
      {/* Number watermark */}
      <span style={{
        position: 'absolute',
        right: 4,
        bottom: -14,
        fontFamily: 'var(--font-display)',
        fontSize: 96,
        fontWeight: 800,
        color: 'rgba(255,255,255,0.04)',
        lineHeight: 1,
        pointerEvents: 'none',
        userSelect: 'none',
        letterSpacing: '-0.04em',
      }}>
        {driver.number}
      </span>

      {/* Headshot + acronym */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, position: 'relative' }}>
        {driver.headshotUrl
          ? <img
              src={driver.headshotUrl}
              alt={driver.acronym}
              style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: `2px solid #${colour}`, background: '#1a1a1a', flexShrink: 0 }}
            />
          : <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: hexToRgba(colour, 0.15),
              border: `2px solid #${colour}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13,
              color: `#${colour}`, flexShrink: 0,
            }}>
              {driver.acronym}
            </div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 20, color: '#f9b000', lineHeight: 1 }}>
            {driver.acronym}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#555', marginTop: 3 }}>
            #{driver.number}
            {driver.countryCode && (() => {
              const url = flagUrl(driver.countryCode);
              return url
                ? <img src={url} alt={driver.countryCode} style={{ height: 11, marginLeft: 8, borderRadius: 1, verticalAlign: 'middle', opacity: 0.85 }} />
                : <span style={{ marginLeft: 8, color: '#888' }}>{driver.countryCode}</span>;
            })()}
            {qualiPos && (
              <span style={{
                marginLeft: 8,
                padding: '1px 5px',
                borderRadius: 3,
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.05em',
                verticalAlign: 'middle',
                ...(qualiPos === 1
                  ? { background: 'rgba(249,176,0,0.15)', color: '#f9b000', border: '1px solid rgba(249,176,0,0.35)' }
                  : { background: 'rgba(255,255,255,0.05)', color: '#666', border: '1px solid #2a2a2a' }
                ),
              }}>
                {qualiPos === 1 ? 'POLE' : `Q${qualiPos}`}
              </span>
            )}
          </div>
        </div>
        {standing && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              P{standing.position}
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, color: '#f0f0f0' }}>
              {standing.points}
              <span style={{ fontSize: 10, color: '#555', marginLeft: 2 }}>pts</span>
            </div>
            {standing.wins > 0 && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#f9b000', marginTop: 1 }}>
                {standing.wins}W
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full name */}
      <div style={{ fontWeight: 500, fontSize: 13, color: '#e0e0e0', position: 'relative', lineHeight: 1.3 }}>
        {driver.fullName}
      </div>
    </div>
  );
}

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [standingsMap, setStandingsMap] = useState({});
  const [qualiMap, setQualiMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      getCurrentDrivers(),
      getDriverStandings().catch(() => []),
    ]).then(([d, s]) => {
      setDrivers(d);
      const map = {};
      for (const entry of s) map[entry.acronym] = entry;
      setStandingsMap(map);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));

    // Independently fetch qualifying positions for the next unlocked race
    getRaces().catch(() => []).then(races => {
      const next = races.find(r => !r.locked && !r.official_top10);
      if (!next) return [];
      return getRaceQualifying(next.id).catch(() => []);
    }).then(quali => {
      if (!quali || !quali.length) return;
      const qmap = {};
      for (const q of quali.slice(0, 10)) qmap[q.code] = q.position;
      setQualiMap(qmap);
    }).catch(() => {});
  }, []);

  // Group by team, assign global animation index
  const teamGroups = {};
  for (const d of drivers) {
    const key = d.team || 'Unknown';
    if (!teamGroups[key]) teamGroups[key] = { colour: d.teamColour, drivers: [] };
    teamGroups[key].drivers.push(d);
  }
  const sortedTeams = Object.entries(teamGroups).sort(([a], [b]) => a.localeCompare(b));
  let cardIdx = 0;
  const teamsWithIdx = sortedTeams.map(([name, group]) => ({
    name,
    colour: group.colour,
    drivers: group.drivers.map(d => ({ ...d, animIdx: cardIdx++ })),
  }));

  const year = new Date().getFullYear();

  return (
    <div>
      <style>{keyframes}</style>

      {/* Header */}
      <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4 }}>
        {year} Season
      </p>
      <h1 style={{
        fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1, marginBottom: 24,
      }}>
        Drivers
      </h1>

      {loading && <p style={{ color: '#666' }}>Loading drivers...</p>}
      {error && <p style={{ color: '#f9b000' }}>Error: {error}</p>}
      {!loading && !error && drivers.length > 0 && drivers.every(d => !d.team || d.team === 'Unknown') && (
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 6, padding: '16px 20px', marginBottom: 24 }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#555' }}>
            ⏳ Driver profiles for the 2026 season are not yet available from OpenF1. Check back during race weekend — data populates as sessions get underway.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {teamsWithIdx.map(team => (
          <div key={team.name}>
            {/* Team header */}
            <div style={{
              background: hexToRgba(team.colour, 0.07),
              borderLeft: `4px solid #${team.colour || '444'}`,
              borderRadius: '6px 6px 0 0',
              padding: '10px 16px',
              marginBottom: 8,
            }}>
              <span style={{
                fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em', color: '#f0f0f0',
              }}>
                {team.name}
              </span>
            </div>

            {/* 2-column driver grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {team.drivers.map(driver => (
                <DriverCard
                  key={driver.acronym || driver.number}
                  driver={driver}
                  animDelay={driver.animIdx * 40}
                  standing={standingsMap[driver.acronym] || null}
                  qualiPos={qualiMap[driver.acronym] || null}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
